import { BellRinging, Compass, ListChecks, Sparkle } from "@phosphor-icons/react";
import { createAction } from "~/actions/registry";
import type { Action } from "~/actions/types";
import { ABILITY } from "~/api/types";
import { useNudgePicker } from "~/features/nudges/useNudgePicker";
import { useTaskizeTray } from "~/features/taskize/useTaskizeTray";
import { useThinkerDock } from "~/features/work/piles/pilesApi";

/**
 * The three *Do* entries, as real registry actions.
 *
 * kms had to fake a registry: `AIActionRegistry` declared `taskize`/`nudge`/`ask` in one place,
 * projected them into a menu in another, and bound ⌘⇧A directly in the prompt bar — so the
 * chord was written down twice and a dead `AIAssistantStore` preference declared it a third
 * time. Here the registry IS the list: ⌘K, the ✨ menu, the ⋯ menu and the Ask bar all read
 * these, and `shortcuts.ts` binds ⌘⇧A because the action says so.
 *
 * The `slash` projection (`/taskize`, `/nudge`, `/ask`) is **dropped** (D6): it was declared in
 * three places and implemented in none, and ⌘K's own search already finds these by name.
 */

const section = (ctx: { t: (key: string) => string }) => ctx.t("AI");

/** Ask mode. The chord lives HERE and nowhere else. */
export const askAi = createAction({
  id: "ai.ask",
  name: (ctx) => ctx.t("Ask AI"),
  section,
  icon: Sparkle,
  keywords: "prompt intent natural language navigate digest remind draft notify",
  shortcut: ["Meta", "Shift", "a"],
  // The bar is a text surface: its whole point is to be reachable while you are typing.
  allowInInput: true,
  perform: (ctx) => {
    ctx.shell.getState().setCommandBarOpen(false);
    useThinkerDock.getState().show();
  },
});

/**
 * Whole-document Taskize. Gated on the document's own `update` — the server gates
 * `ai-actions/taskize` on authentication ALONE (§E2 R2), so this affordance is the access
 * control and must never be widened.
 */
export const taskizeDocument = createAction({
  id: "ai.taskize",
  name: (ctx) => ctx.t("Taskize this document"),
  section,
  icon: ListChecks,
  keywords: "tasks decisions reminders extract actions todo",
  visible: (ctx) =>
    Boolean(ctx.activeEntityId) &&
    ctx.abilities(ctx.activeEntityId ?? "")[ABILITY.documentUpdate] === true,
  perform: (ctx) => {
    const documentId = ctx.activeEntityId;
    if (!documentId) {
      return;
    }
    // The tray is scene-owned; the shell asks and the scene answers (§C1.2).
    useTaskizeTray.getState().requestTaskize(documentId);
  },
});

/** "Nudge me…" — the unanchored trigger; the picker centres itself without a rect. */
export const nudgeAboutDocument = createAction({
  id: "ai.nudge",
  name: (ctx) => ctx.t("Remind me about this…"),
  section,
  icon: BellRinging,
  keywords: "nudge reminder later snooze quickwhen remind",
  visible: (ctx) => Boolean(ctx.activeEntityId),
  perform: (ctx) => {
    const documentId = ctx.activeEntityId;
    if (!documentId) {
      return;
    }
    // No anchor and no note: the ⋯/⌘K trigger has no rect and no selection to quote, and the
    // picker's own note field is where a person says what the reminder is about.
    useNudgePicker.getState().openPicker({
      target: { targetType: "document", documentId },
    });
  },
});

/**
 * Re-entry into the first-run guide, from the *Help* section.
 *
 * ⚠ The three navigation actions the kms delta also added — Task Center, Run Center, Flow
 * Definitions — are **deliberately not declared here**. Slate generates one command-bar entry
 * per settings section from the registry, so those three already exist; declaring them again
 * is precisely the double-registration defect (control-plane D-10) that made ⌘K list each of
 * them twice in kms. One registration site per action.
 */
export const openWelcomeGuide = createAction({
  id: "ai.welcomeGuide",
  name: (ctx) => ctx.t("Open the welcome guide"),
  section: (ctx) => ctx.t("Help"),
  icon: Compass,
  keywords: "welcome guide onboarding wizard getting started seed",
  perform: (ctx) => ctx.shell.getState().openDialog({ kind: "welcomeGuide" }),
});

export const aiActionsList: Action[] = [
  askAi,
  taskizeDocument,
  nudgeAboutDocument,
  openWelcomeGuide,
];

/**
 * The two entries the document ⋯ menu offers. `ai.ask` is deliberately not among them: the bar
 * is a global surface with a chord, not a per-document verb.
 */
export const documentAiActions: Action[] = [taskizeDocument, nudgeAboutDocument];
